import { CheckCircleIcon } from '@heroicons/react/24/outline';
import type { SubmissionStepMeta } from './submissionSteps';
import type { StepProgress } from './submissionProgress';

interface SubmissionStepTabsProps {
    steps: readonly SubmissionStepMeta[];
    progress: readonly StepProgress[];
    stepIndex: number;
    onSelect: (index: number) => void;
}

function pillClass(isActive: boolean, isComplete: boolean): string {
    if (isActive) return 'bg-gray-900 text-white';
    if (isComplete) {
        return 'bg-green-50 text-green-700 border border-green-500 hover:bg-green-100';
    }
    return 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100 hover:text-gray-700';
}

/**
 * Pill navigation modeled on the clinical wizards' WizardStepTabs, plus a
 * completed state: a complete step swaps its icon for a green check. Every
 * step is freely clickable since the viewer has nothing to validate.
 */
export function SubmissionStepTabs({
    steps,
    progress,
    stepIndex,
    onSelect
}: SubmissionStepTabsProps) {
    return (
        <nav
            aria-label="Submission steps"
            className="flex items-center justify-center flex-wrap gap-y-2 gap-x-1 min-w-0"
        >
            {steps.map((step, index) => {
                const isActive = index === stepIndex;
                const isComplete = progress.find((p) => p.id === step.id)?.complete ?? false;
                const Icon = isComplete ? CheckCircleIcon : step.icon;
                const iconColor = isComplete
                    ? isActive
                        ? 'text-green-400'
                        : 'text-green-500'
                    : '';
                return (
                    <div key={step.id} className="flex items-center">
                        <button
                            type="button"
                            onClick={() => onSelect(index)}
                            aria-current={isActive ? 'step' : undefined}
                            aria-label={`${step.label}: ${isComplete ? 'complete' : 'incomplete'}`}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 ${pillClass(isActive, isComplete)}`}
                        >
                            <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                            <span>{step.label}</span>
                        </button>
                        {index < steps.length - 1 && <div className="w-4 h-px mx-1 bg-gray-200" />}
                    </div>
                );
            })}
        </nav>
    );
}
